// src/pages/Home.js
import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { CartContext } from "../context/CartContext";

const LOCAL_KEY = "ln_reviews_local_v3";

// Seed reviews for first run
const seedReviews = [
  {
    id: "seed-1",
    name: "Aisha",
    rating: 5,
    reviewText: "Absolutely loved these press-ons! Fit perfectly and lasted two weeks.",
    imageDataUrl: "/Review1.jpg",
    createdAt: new Date().toISOString(),
  },
  {
    id: "seed-2",
    name: "Maya",
    rating: 4,
    reviewText: "Cute designs and quick shipping. The sparkle is so pretty!",
    imageDataUrl: "/Review2.jpg",
    createdAt: new Date().toISOString(),
  },
  {
    id: "seed-3",
    name: "Sam",
    rating: 5,
    reviewText: "Great quality for the price. Lisa was super helpful with sizing.",
    imageDataUrl: null,
    createdAt: new Date().toISOString(),
  },
];

export default function Home() {
  const { addToCart } = useContext(CartContext);

  const presets = useMemo(
    () => [
      { id: 1, img: "/Preset1.jpg", name: "Preset Design 1", price: 25 },
      { id: 2, img: "/Preset2.jpg", name: "Preset Design 2", price: 30 },
      { id: 3, img: "/Preset3.jpg", name: "Preset Design 3", price: 28 },
      { id: 4, img: "/Preset4.jpg", name: "Preset Design 4", price: 32 },
      { id: 5, img: "/Preset5.jpg", name: "Preset Design 5", price: 29 },
      { id: 6, img: "/Preset6.jpg", name: "Preset Design 6", price: 30 },
    ],
    []
  );

  // reviews state (local-storage backed)
  const [reviews, setReviews] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);

  // 🆕 per-ad open/close state
  const [adOpen, setAdOpen] = useState({
    left1: true,
    left2: true,
    right1: true,
    right2: true,
  });

  const toggleAd = (key) => {
    setAdOpen((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // form state
  const [name, setName] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(5);
  const [imageFile, setImageFile] = useState(null); // File object
  const [imagePreview, setImagePreview] = useState(null); // data URL
  const [errorMsg, setErrorMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const scrollerRef = useRef(null);

  // ✅ Typing animation with looping every 5 seconds
  const fullText = "Welcome to LisaNails 💅";
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    let i = 0;

    const type = () => {
      setTypedText("");
      i = 0;

      const typingInterval = setInterval(() => {
        setTypedText(fullText.slice(0, i + 1));
        i++;
        if (i === fullText.length) {
          clearInterval(typingInterval);
          setTimeout(type, 3000);
        }
      }, 100);
    };

    type();

    return () => {};
  }, []);

  // load from localStorage safely
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LOCAL_KEY);
      if (!raw) {
        localStorage.setItem(LOCAL_KEY, JSON.stringify(seedReviews));
        setReviews(seedReviews);
      } else {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setReviews(parsed);
        else {
          localStorage.setItem(LOCAL_KEY, JSON.stringify(seedReviews));
          setReviews(seedReviews);
        }
      }
    } catch (err) {
      console.error("Failed reading reviews from localStorage:", err);
      setReviews(seedReviews);
    }

    const onUnhandledRejection = (evt) => {
      console.error("=== Page-level unhandledrejection ===");
      console.error("reason:", evt.reason);
      try {
        console.error(
          "stringified reason:",
          JSON.stringify(evt.reason, Object.getOwnPropertyNames(evt.reason))
        );
      } catch (e) {
        console.error("stringify failed", e);
      }
      if (evt.reason && evt.reason.stack) console.error("stack:", evt.reason.stack);
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", onUnhandledRejection);
  }, []);

  // helper: save reviews safely
  const persistReviews = (next) => {
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
    } catch (err) {
      console.error("Failed to write reviews to localStorage:", err);
    }
  };

  const handleAddToCart = (preset) => {
    try {
      addToCart({
        shape: "Preset",
        length: "Standard",
        color: "Design",
        ...preset,
      });
      setSelectedPreset(null);
    } catch (err) {
      console.error("addToCart failed:", err);
    }
  };

  // file upload handling (validates + creates data URL)
  const handleImageSelect = (e) => {
    setErrorMsg("");
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setErrorMsg("Only JPG / PNG / WEBP images allowed.");
      setImageFile(null);
      setImagePreview(null);
      e.target.value = "";
      return;
    }

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setErrorMsg("Image too large. Max 5 MB.");
      setImageFile(null);
      setImagePreview(null);
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result && typeof reader.result === "string") {
        setImagePreview(reader.result);
        setImageFile(file);
      } else {
        setErrorMsg("Failed to read image.");
        setImageFile(null);
        setImagePreview(null);
      }
    };
    reader.onerror = (err) => {
      console.error("FileReader error:", err);
      setErrorMsg("Failed to read image file.");
      setImageFile(null);
      setImagePreview(null);
    };
    try {
      reader.readAsDataURL(file);
    } catch (err) {
      console.error("readAsDataURL threw:", err);
      setErrorMsg("Failed to process image file.");
      setImageFile(null);
      setImagePreview(null);
    }
  };

  // submit review (local only)
  const handleSubmitReview = (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!name.trim() || !reviewText.trim() || !rating) {
      setErrorMsg("Please enter your name, rating and a short review.");
      return;
    }

    setSubmitting(true);
    try {
      const newReview = {
        id: `local-${Date.now()}`,
        name: name.trim(),
        rating: Number(rating),
        reviewText: reviewText.trim(),
        imageDataUrl: imagePreview || null,
        createdAt: new Date().toISOString(),
      };

      const next = [newReview, ...reviews];
      setReviews(next);
      persistReviews(next);

      setName("");
      setReviewText("");
      setRating(5);
      setImageFile(null);
      setImagePreview(null);
      setErrorMsg("");

      setTimeout(() => {
        if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
      }, 120);
    } catch (err) {
      console.error("Failed to add review:", err);
      setErrorMsg("Failed to add review.");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollBy = (delta) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: delta, behavior: "smooth" });
  };

  const fmtDate = (iso) => {
    try {
      if (!iso) return "";
      return new Date(iso).toLocaleDateString();
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-col min-h-screen text-white bg-gradient-to-b from-pink-900 via-purple-900 to-black relative">
      <Navbar />

      {/* 🆕 Floating individual video ads – LEFT side */}
      <div className="fixed top-24 left-2 z-40 flex flex-col gap-3">
        {/* Left ad 1 */}
        <div
          className={`relative transition-transform duration-500 ease-out ${
            adOpen.left1 ? "translate-x-0" : "-translate-x-40"
          }`}
        >
          <div className="relative bg-black/80 rounded-xl shadow-xl overflow-hidden border border-pink-500/70 w-44">
            <video
              src="/popup-ad1.mp4"
              autoPlay
              muted
              loop
              playsInline
              controls
              className="w-full h-auto object-contain bg-black"
            />
          </div>
          <button
            type="button"
            onClick={() => toggleAd("left1")}
            className="absolute top-1/2 -right-3 -translate-y-1/2 bg-pink-500 hover:bg-pink-600 text-white text-xs px-2 py-1 rounded-full shadow-md"
            title={adOpen.left1 ? "Hide ad" : "Show ad"}
          >
            {adOpen.left1 ? "⟵" : "⟶"}
          </button>
        </div>

        {/* Left ad 2 */}
        <div
          className={`relative transition-transform duration-500 ease-out ${
            adOpen.left2 ? "translate-x-0" : "-translate-x-40"
          }`}
        >
          <div className="relative bg-black/80 rounded-xl shadow-xl overflow-hidden border border-pink-500/70 w-44">
            <video
              src="/popup-ad2.mp4"
              autoPlay
              muted
              loop
              playsInline
              controls
              className="w-full h-auto object-contain bg-black"
            />
          </div>
          <button
            type="button"
            onClick={() => toggleAd("left2")}
            className="absolute top-1/2 -right-3 -translate-y-1/2 bg-pink-500 hover:bg-pink-600 text-white text-xs px-2 py-1 rounded-full shadow-md"
            title={adOpen.left2 ? "Hide ad" : "Show ad"}
          >
            {adOpen.left2 ? "⟵" : "⟶"}
          </button>
        </div>
      </div>

      {/* 🆕 Floating individual video ads – RIGHT side */}
      <div className="fixed top-24 right-2 z-40 flex flex-col gap-3 items-end">
        {/* Right ad 1 */}
        <div
          className={`relative transition-transform duration-500 ease-out ${
            adOpen.right1 ? "translate-x-0" : "translate-x-40"
          }`}
        >
          <div className="relative bg-black/80 rounded-xl shadow-xl overflow-hidden border border-pink-500/70 w-44">
            <video
              src="/popup-ad3.mp4"
              autoPlay
              muted
              loop
              playsInline
              controls
              className="w-full h-auto object-contain bg-black"
            />
          </div>
          <button
            type="button"
            onClick={() => toggleAd("right1")}
            className="absolute top-1/2 -left-3 -translate-y-1/2 bg-pink-500 hover:bg-pink-600 text-white text-xs px-2 py-1 rounded-full shadow-md"
            title={adOpen.right1 ? "Hide ad" : "Show ad"}
          >
            {adOpen.right1 ? "⟶" : "⟵"}
          </button>
        </div>

        {/* Right ad 2 */}
        <div
          className={`relative transition-transform duration-500 ease-out ${
            adOpen.right2 ? "translate-x-0" : "translate-x-40"
          }`}
        >
          <div className="relative bg-black/80 rounded-xl shadow-xl overflow-hidden border border-pink-500/70 w-44">
            <video
              src="/popup-ad4.mp4"
              autoPlay
              muted
              loop
              playsInline
              controls
              className="w-full h-auto object-contain bg-black"
            />
          </div>
          <button
            type="button"
            onClick={() => toggleAd("right2")}
            className="absolute top-1/2 -left-3 -translate-y-1/2 bg-pink-500 hover:bg-pink-600 text-white text-xs px-2 py-1 rounded-full shadow-md"
            title={adOpen.right2 ? "Hide ad" : "Show ad"}
          >
            {adOpen.right2 ? "⟶" : "⟵"}
          </button>
        </div>
      </div>

      {/* background */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30 z-0"
        style={{ backgroundImage: 'url("/nails.jpeg")' }}
      />

      <main className="relative z-10 flex-grow flex flex-col items-center px-6 pt-32">
        <h1 className="text-6xl font-extrabold text-pink-400 drop-shadow-lg mb-6 text-center">
          {typedText || "Welcome to LisaNails 💅"}
          <span className="inline-block align-middle ml-1 w-[6px] h-[1em] bg-pink-400 animate-blink"></span>
        </h1>

        <p className="text-lg text-white/90 max-w-2xl mx-auto mb-12 text-center">
          Design your dream nails online or shop from our preset press-on nail
          designs ready to ship!
        </p>

        {/* Presets grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-12 mb-12 w-full max-w-7xl">
          {presets.map((preset) => (
            <div
              key={preset.id}
              className="bg-white/10 backdrop-blur-md rounded-xl p-6 shadow-lg hover:scale-105 transition cursor-pointer"
              onClick={() => setSelectedPreset(preset)}
            >
              <div className="w-full h-64 flex items-center justify-center bg-white/5 rounded-lg overflow-hidden">
                <img
                  src={preset.img}
                  alt={preset.name}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <h3 className="text-pink-400 font-semibold mt-4 text-xl">
                {preset.name}
              </h3>
              <p className="text-white/80 text-lg">${preset.price}</p>
            </div>
          ))}
        </div>

        {/* Reviews header */}
        <div className="w-full max-w-7xl mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between px-2 gap-3">
          <h2 className="text-3xl font-bold text-pink-400">Customer Reviews</h2>
        </div>

        {/* layout: form + carousel */}
        <div className="w-full max-w-7xl grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {/* form */}
          <div className="lg:col-span-1 bg-white/5 backdrop-blur-md p-6 rounded-xl">
            <form onSubmit={handleSubmitReview} className="flex flex-col gap-3">
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="px-3 py-2 rounded-md text-black"
                disabled={submitting}
              />
              <textarea
                placeholder="Write your review..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="px-3 py-2 rounded-md text-black h-28"
                disabled={submitting}
              />

              <div className="flex items-center gap-2">
                {Array.from({ length: 5 }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    className={`text-2xl ${
                      n <= rating ? "text-yellow-400" : "text-white/40"
                    }`}
                    disabled={submitting}
                    aria-label={`${n} star`}
                  >
                    ★
                  </button>
                ))}
              </div>

              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  disabled={submitting}
                  className="hidden"
                />
                <div className="px-3 py-2 bg-white/10 rounded text-white/80">
                  {imageFile ? "Change image" : "Upload image (optional)"}
                </div>
                <div className="text-white/70 text-sm">
                  {imageFile ? imageFile.name : "Max 5MB"}
                </div>
              </label>

              {imagePreview && (
                <div className="mt-2">
                  <div className="w-full h-36 rounded overflow-hidden bg-white/5 flex items-center justify-center">
                    <img
                      src={imagePreview}
                      alt="preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                </div>
              )}

              {errorMsg && <div className="text-sm text-red-400">{errorMsg}</div>}

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="mt-2 bg-pink-500 hover:bg-pink-600 px-4 py-2 rounded-md text-white"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit Review"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setName("");
                    setReviewText("");
                    setRating(5);
                    setImageFile(null);
                    setImagePreview(null);
                    setErrorMsg("");
                  }}
                  className="mt-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-md text-white"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>

          {/* carousel */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <div className="text-white/80">Latest reviews</div>
              <div className="flex gap-2">
                <button
                  onClick={() => scrollBy(-320)}
                  className="px-3 py-1 bg-white/10 rounded hover:bg-white/20"
                  aria-label="Scroll left"
                >
                  ◀
                </button>
                <button
                  onClick={() => scrollBy(320)}
                  className="px-3 py-1 bg-white/10 rounded hover:bg-white/20"
                  aria-label="Scroll right"
                >
                  ▶
                </button>
              </div>
            </div>

            <div
              ref={scrollerRef}
              className="overflow-x-auto no-scrollbar whitespace-nowrap gap-4 py-2 px-1"
              style={{ scrollBehavior: "smooth" }}
            >
              {reviews.length === 0 ? (
                <div className="inline-block px-4 py-8 text-white/60">
                  No reviews yet — be the first!
                </div>
              ) : (
                reviews.map((r) => (
                  <div
                    key={r.id}
                    className="inline-block align-top bg-white/5 backdrop-blur-md rounded-xl p-4 mr-4 w-72"
                    style={{
                      verticalAlign: "top",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedReview(r)}
                  >
                    <div className="w-full h-40 mb-3 rounded overflow-hidden bg-white/4 flex items-center justify-center">
                      {r.imageDataUrl ? (
                        <img
                          src={r.imageDataUrl}
                          alt={`${r.name} photo`}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <div className="text-white/50">No photo</div>
                      )}
                    </div>

                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <div className="text-white font-semibold">{r.name}</div>
                        <div className="text-xs text-white/60">
                          {fmtDate(r.createdAt)}
                        </div>
                      </div>
                      <div className="text-yellow-400">
                        {Array.from({ length: 5 }, (_, i) => (
                          <span key={i}>
                            {i < (r.rating || 0) ? "★" : "☆"}
                          </span>
                        ))}
                      </div>
                    </div>

                    <p
                      className="text-white/80 mt-3 break-words"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {r.reviewText}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* how-to video */}
        <section className="how-to-apply my-12 w-full max-w-3xl mx.auto px-4">
          <h3 className="text-3xl font-bold text-pink-400 mb-4 text-center">
            How to Apply Press-On Nails
          </h3>
          <div className="relative w-full pb-[56.25%] rounded-lg overflow-hidden shadow-lg">
            <iframe
              src="https://www.youtube.com/embed/IAomxaX_N1k"
              title="How to Apply Press On Nails"
              className="absolute top-0 left-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </section>
      </main>

      {/* preset modal */}
      {selectedPreset && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white text-black rounded-xl shadow-lg p-8 w-4/5 max-w-5xl flex gap-12 relative">
            <button
              className="absolute top-3 right-3 text-2xl font-bold"
              onClick={() => setSelectedPreset(null)}
            >
              ✕
            </button>
            <div className="flex-1 flex.items-center justify-center">
              <img
                src={selectedPreset.img}
                alt={selectedPreset.name}
                className="max-w-full max-h-[520px] object-contain rounded-lg"
              />
            </div>
            <div className="flex-1 flex flex-col justify-center">
              <h2 className="text-3xl font-bold mb-6">
                {selectedPreset.name}
              </h2>
              <p className="text-2xl mb-6 font-semibold text-pink-600">
                ${selectedPreset.price}
              </p>
              <button
                onClick={() => handleAddToCart(selectedPreset)}
                className="px-8 py-4 bg-pink-500 text-white rounded-lg shadow-lg hover:bg-pink-600 transition text-lg"
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* review modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white text-black rounded-xl shadow-lg p-8 w-full max-w-2xl relative">
            <button
              className="absolute top-3 right-3 text-2xl font-bold"
              onClick={() => setSelectedReview(null)}
            >
              ✕
            </button>
            <div className="w-full mb-6 flex items-center justify-center">
              {selectedReview.imageDataUrl ? (
                <img
                  src={selectedReview.imageDataUrl}
                  alt={selectedReview.name}
                  className="w-full h-auto max-h-[520px] object-contain rounded-lg"
                />
              ) : (
                <div className="w-full h-40 rounded bg-white/4 flex items-center justify-center text-white/50 mb-6">
                  No photo
                </div>
              )}
            </div>

            <div className="flex items-center mb-4">
              {Array(selectedReview.rating)
                .fill("★")
                .map((s, i) => (
                  <span key={i} className="text-yellow-500 text-xl">
                    {s}
                  </span>
                ))}
            </div>

            <p className="text-lg mb-4 italic break-words">
              {selectedReview.reviewText}
            </p>
            <p className="text-right font-semibold text-pink-600">
              - {selectedReview.name}
            </p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
